import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { LesseeLeaseCard } from '../components/leasing/LesseeLeaseCard';
import { LesseeLeaseMaintenance } from '../components/leasing/LesseeLeaseMaintenance';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Car, ArrowLeft, User, Calendar, Fuel, AlertTriangle, Camera,
    Wrench, Clock, MapPin, CheckCircle, XCircle, Loader2,
    ChevronLeft, ChevronRight, FileText, CreditCard, Shield,
    Phone, Edit, History, TrendingUp, Package, Bell, Truck,
    Download, ZoomIn, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';

export default function VehicleDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { canManage } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [vehicle, setVehicle] = useState(null);
    const [inspections, setInspections] = useState([]);
    const [maintenanceRecords, setMaintenanceRecords] = useState([]);
    const [damageAlerts, setDamageAlerts] = useState([]);
    const [assignedDriver, setAssignedDriver] = useState(null);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
    const [allPhotos, setAllPhotos] = useState([]);
    
    // New states for photo features
    const [photoFilter, setPhotoFilter] = useState('all'); // 'all', 'checkin', 'checkout'
    const [zoomModalOpen, setZoomModalOpen] = useState(false);
    const [zoomPhotoIndex, setZoomPhotoIndex] = useState(0);

    useEffect(() => {
        if (id) {
            fetchVehicleData();
        }
    }, [id]);

    const fetchVehicleData = async () => {
        setLoading(true);
        try {
            // Fetch vehicle
            const vehicleRes = await api.get(`/vehicles/${id}`);
            setVehicle(vehicleRes.data);

            // Fetch inspections
            try {
                const inspectionsRes = await api.get(`/inspections?vehicle_id=${id}`);
                setInspections(inspectionsRes.data || []);
                
                // Collect all photos from inspections
                const photos = [];
                for (const inspection of (inspectionsRes.data || [])) {
                    if (inspection.photos) {
                        inspection.photos.forEach(p => {
                            photos.push({
                                ...p,
                                type: 'checkin',
                                url: p.storage_path,
                                date: inspection.created_at,
                                inspection_id: inspection.id
                            });
                        });
                    }
                    if (inspection.checkout_photos) {
                        inspection.checkout_photos.forEach(p => {
                            photos.push({
                                ...p,
                                type: 'checkout',
                                url: p.path,
                                date: inspection.checkout_date || inspection.created_at,
                                inspection_id: inspection.id
                            });
                        });
                    }
                }
                setAllPhotos(photos);
            } catch (e) {
                console.error('Failed to fetch inspections:', e);
            }

            // Fetch maintenance records
            try {
                const maintenanceRes = await api.get(`/maintenance?vehicle_id=${id}`);
                setMaintenanceRecords(maintenanceRes.data || []);
            } catch (e) {
                console.error('Failed to fetch maintenance:', e);
            }

            // Fetch damage alerts
            try {
                const damagesRes = await api.get(`/photo-comparison/alerts`);
                setDamageAlerts((damagesRes.data || []).filter(d => d.vehicle_id === id));
            } catch (e) {
                console.error('Failed to fetch damages:', e);
            }

            // Fetch assigned driver
            if (vehicleRes.data.assigned_driver_id) {
                try {
                    const driverRes = await api.get(`/drivers/${vehicleRes.data.assigned_driver_id}`);
                    setAssignedDriver(driverRes.data);
                } catch (e) {
                    console.error('Failed to fetch driver:', e);
                }
            }

        } catch (error) {
            console.error('Failed to fetch vehicle:', error);
            toast.error(language === 'el' ? 'Αποτυχία φόρτωσης οχήματος' : 'Failed to load vehicle');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getPhotoUrl = async (photo) => {
        // This is now a placeholder - we use fetchPhotoAsBlob instead
        if (!photo) return null;
        const path = photo.url || photo.storage_path || photo.path;
        if (!path) return null;
        if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path;
        return null;
    };

    // Fetch photo as blob with authentication
    const fetchPhotoAsBlob = async (photo) => {
        if (!photo) return null;
        
        // If already a blob or data URL, return as is
        if (photo.blobUrl) return photo.blobUrl;
        
        const path = photo.url || photo.storage_path || photo.path;
        if (!path) return null;
        
        // If already an HTTP URL, try to use it
        if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
            return path;
        }
        
        // Try inspection endpoint first
        if (photo.inspection_id && photo.photo_type) {
            try {
                const response = await api.get(
                    `/inspections/${photo.inspection_id}/photos/${photo.photo_type}`,
                    { responseType: 'blob' }
                );
                const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
                return URL.createObjectURL(blob);
            } catch (e) {
                console.log('Failed to fetch from inspection endpoint:', e);
            }
        }
        
        // Try files endpoint
        try {
            const response = await api.get(`/files/${path}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
            return URL.createObjectURL(blob);
        } catch (e) {
            console.log('Failed to fetch from files endpoint:', e);
        }
        
        return null;
    };

    // State for photo blob URLs
    const [photoBlobUrls, setPhotoBlobUrls] = useState({});

    // Load photo blobs when photos change
    useEffect(() => {
        const loadPhotos = async () => {
            const urls = {};
            for (let i = 0; i < allPhotos.length; i++) {
                const photo = allPhotos[i];
                const url = await fetchPhotoAsBlob(photo);
                if (url) {
                    urls[i] = url;
                }
            }
            setPhotoBlobUrls(urls);
        };
        
        if (allPhotos.length > 0) {
            loadPhotos();
        }
        
        // Cleanup blob URLs on unmount
        return () => {
            Object.values(photoBlobUrls).forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [allPhotos]);

    // Filter photos based on type
    const filteredPhotos = allPhotos.filter(photo => {
        if (photoFilter === 'all') return true;
        if (photoFilter === 'checkin') return photo.type === 'checkin';
        if (photoFilter === 'checkout') return photo.type === 'checkout';
        return true;
    });

    // Get filtered photo blob URLs
    const getFilteredPhotoIndex = (originalIndex) => {
        const photo = allPhotos[originalIndex];
        return filteredPhotos.findIndex(p => p === photo);
    };

    // Download photo function
    const downloadPhoto = useCallback(async (photoIndex) => {
        const photo = filteredPhotos[photoIndex];
        if (!photo) return;
        
        // Find the original index in allPhotos
        const originalIndex = allPhotos.findIndex(p => p === photo);
        const blobUrl = photoBlobUrls[originalIndex];
        
        if (!blobUrl) {
            toast.error(language === 'el' ? 'Η φωτογραφία δεν είναι διαθέσιμη' : 'Photo not available');
            return;
        }
        
        try {
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Create filename
            const date = photo.date || new Date().toISOString().split('T')[0];
            const type = photo.photo_type || photo.type || 'photo';
            const inspType = photo.type === 'checkout' ? 'checkout' : 'checkin';
            link.download = `${vehicle?.license_plate || 'vehicle'}_${inspType}_${type}_${date}.jpg`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success(language === 'el' ? 'Η φωτογραφία κατέβηκε' : 'Photo downloaded');
        } catch (error) {
            console.error('Download failed:', error);
            toast.error(language === 'el' ? 'Αποτυχία λήψης' : 'Download failed');
        }
    }, [filteredPhotos, allPhotos, photoBlobUrls, vehicle, language]);

    // Open zoom modal
    const openZoomModal = (photoIndex) => {
        setZoomPhotoIndex(photoIndex);
        setZoomModalOpen(true);
    };

    // Navigate in zoom modal
    const navigateZoom = (direction) => {
        const newIndex = zoomPhotoIndex + direction;
        if (newIndex >= 0 && newIndex < filteredPhotos.length) {
            setZoomPhotoIndex(newIndex);
        }
    };

    // Get blob URL for filtered photo
    const getFilteredBlobUrl = (filteredIndex) => {
        const photo = filteredPhotos[filteredIndex];
        if (!photo) return null;
        const originalIndex = allPhotos.findIndex(p => p === photo);
        return photoBlobUrls[originalIndex];
    };

    const fuelLevelConfig = {
        empty: { label: language === 'el' ? 'Άδειο' : 'Empty', color: 'text-red-500', bg: 'bg-red-500', percent: 0 },
        quarter: { label: '1/4', color: 'text-orange-500', bg: 'bg-orange-500', percent: 25 },
        half: { label: '1/2', color: 'text-yellow-500', bg: 'bg-yellow-500', percent: 50 },
        three_quarter: { label: '3/4', color: 'text-green-400', bg: 'bg-green-400', percent: 75 },
        full: { label: language === 'el' ? 'Γεμάτο' : 'Full', color: 'text-green-600', bg: 'bg-green-600', percent: 100 }
    };

    const statusColors = {
        active: 'bg-green-100 text-green-700',
        maintenance: 'bg-yellow-100 text-yellow-700',
        out_of_service: 'bg-red-100 text-red-700'
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    if (!vehicle) {
        return (
            <div className="text-center py-16">
                <Car className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h2 className="text-xl font-semibold text-slate-600">
                    {language === 'el' ? 'Το όχημα δεν βρέθηκε' : 'Vehicle not found'}
                </h2>
                <Button onClick={() => navigate('/app/vehicles')} className="mt-4">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {language === 'el' ? 'Επιστροφή' : 'Go Back'}
                </Button>
            </div>
        );
    }

    const lastInspection = inspections[0];
    const lastFuelLevel = lastInspection?.fuel_level;

    return (
        <div className="space-y-6" data-testid="vehicle-details-page">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate('/app/vehicles')}
                        className="text-slate-600 hover:text-slate-800"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-16 h-16 bg-[#1B7EB5] rounded-xl flex items-center justify-center">
                        <Truck className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">
                            {vehicle.make} {vehicle.model}
                        </h1>
                        <div className="flex items-center gap-3 mt-1">
                            <code className="text-lg font-mono text-[#1B7EB5]">{vehicle.license_plate}</code>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[vehicle.status] || statusColors.active}`}>
                                {vehicle.status}
                            </span>
                            {vehicle.is_replacement && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                                    {language === 'el' ? 'Αντικατάσταση' : 'Replacement'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {canManage() && (
                    <Button 
                        onClick={() => navigate(`/app/vehicles?edit=${vehicle.id}`)}
                        className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90"
                    >
                        <Edit className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                    </Button>
                )}
            </div>

            {vehicle.leased_by_company_id && <LesseeLeaseCard vehicleId={vehicle.id} el={language === 'el'} />}
            {vehicle.leased_by_company_id && <LesseeLeaseMaintenance vehicleId={vehicle.id} el={language === 'el'} />}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <MapPin className="w-4 h-4" />
                        {language === 'el' ? 'Χιλιόμετρα' : 'Mileage'}
                    </div>
                    <div className="text-2xl font-bold text-slate-800">
                        {vehicle.current_mileage?.toLocaleString() || 0} <span className="text-sm font-normal">km</span>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <Fuel className="w-4 h-4" />
                        {language === 'el' ? 'Καύσιμα' : 'Fuel'}
                    </div>
                    {lastFuelLevel ? (
                        <div className="space-y-2">
                            <div className={`text-2xl font-bold ${fuelLevelConfig[lastFuelLevel]?.color}`}>
                                {fuelLevelConfig[lastFuelLevel]?.label}
                            </div>
                            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full ${fuelLevelConfig[lastFuelLevel]?.bg}`}
                                    style={{ width: `${fuelLevelConfig[lastFuelLevel]?.percent}%` }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="text-slate-400">{language === 'el' ? 'Δεν υπάρχουν δεδομένα' : 'No data'}</div>
                    )}
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <User className="w-4 h-4" />
                        {language === 'el' ? 'Οδηγός' : 'Driver'}
                    </div>
                    {assignedDriver ? (
                        <div>
                            <div className="font-semibold text-slate-800">{assignedDriver.name}</div>
                            <div className="text-xs text-slate-500">{assignedDriver.phone || assignedDriver.email}</div>
                        </div>
                    ) : (
                        <div className="text-slate-400">{language === 'el' ? 'Μη ανατεθειμένο' : 'Unassigned'}</div>
                    )}
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <Clock className="w-4 h-4" />
                        {language === 'el' ? 'Τελευταίος Έλεγχος' : 'Last Inspection'}
                    </div>
                    {lastInspection ? (
                        <div>
                            <div className="font-semibold text-slate-800">{formatDate(lastInspection.created_at)}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-1">
                                {lastInspection.checkout_completed ? (
                                    <><CheckCircle className="w-3 h-3 text-green-500" /> {language === 'el' ? 'Ολοκληρωμένο' : 'Completed'}</>
                                ) : (
                                    <><Clock className="w-3 h-3 text-orange-500" /> {language === 'el' ? 'Σε εξέλιξη' : 'In progress'}</>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-slate-400">{language === 'el' ? 'Κανένας έλεγχος' : 'No inspection'}</div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="bg-slate-100 p-1">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-white">
                        <FileText className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Επισκόπηση' : 'Overview'}
                    </TabsTrigger>
                    <TabsTrigger value="photos" className="data-[state=active]:bg-white">
                        <Camera className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Φωτογραφίες' : 'Photos'} ({allPhotos.length})
                    </TabsTrigger>
                    <TabsTrigger value="maintenance" className="data-[state=active]:bg-white">
                        <Wrench className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Συντήρηση' : 'Maintenance'} ({maintenanceRecords.length})
                    </TabsTrigger>
                    <TabsTrigger value="damages" className="data-[state=active]:bg-white">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Ζημιές' : 'Damages'} ({damageAlerts.length})
                    </TabsTrigger>
                    <TabsTrigger value="inspections" className="data-[state=active]:bg-white">
                        <History className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Ιστορικό' : 'History'} ({inspections.length})
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-4">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Vehicle Info Card */}
                        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                <Car className="w-5 h-5 text-[#1B7EB5]" />
                                {language === 'el' ? 'Στοιχεία Οχήματος' : 'Vehicle Info'}
                            </h3>
                            <div className="space-y-3">
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Μάρκα' : 'Make'}</span>
                                    <span className="font-medium text-slate-800">{vehicle.make}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Μοντέλο' : 'Model'}</span>
                                    <span className="font-medium text-slate-800">{vehicle.model}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Έτος' : 'Year'}</span>
                                    <span className="font-medium text-slate-800">{vehicle.year}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Τύπος' : 'Type'}</span>
                                    <span className="font-medium text-slate-800 capitalize">{vehicle.vehicle_type}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Καύσιμο' : 'Fuel Type'}</span>
                                    <span className="font-medium text-slate-800 capitalize">{vehicle.fuel_type}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">VIN</span>
                                    <span className="font-mono text-sm text-slate-800">{vehicle.vin}</span>
                                </div>
                                <div className="flex justify-between py-2">
                                    <span className="text-slate-500">{language === 'el' ? 'Ιδιοκτησία' : 'Ownership'}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        vehicle.ownership === 'leasing' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                                    }`}>
                                        {vehicle.ownership === 'leasing' ? 'Leasing' : (language === 'el' ? 'Ιδιόκτητο' : 'Owned')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Insurance & Registration Card */}
                        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                <Shield className="w-5 h-5 text-[#1B7EB5]" />
                                {language === 'el' ? 'Ασφάλεια & Έγγραφα' : 'Insurance & Documents'}
                            </h3>
                            <div className="space-y-3">
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Ημ. Εγγραφής' : 'Registration Date'}</span>
                                    <span className="font-medium text-slate-800">{formatDate(vehicle.registration_date) || '-'}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Λήξη Ασφάλειας' : 'Insurance Expiry'}</span>
                                    <span className={`font-medium ${
                                        vehicle.insurance_expiry && new Date(vehicle.insurance_expiry) < new Date() 
                                            ? 'text-red-600' 
                                            : 'text-slate-800'
                                    }`}>
                                        {formatDate(vehicle.insurance_expiry) || '-'}
                                    </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-slate-100">
                                    <span className="text-slate-500">{language === 'el' ? 'Αρ. Ελαστικών' : 'Tyre Number'}</span>
                                    <span className="font-medium text-slate-800">{vehicle.tyre_number || '-'}</span>
                                </div>
                                <div className="flex justify-between py-2">
                                    <span className="text-slate-500">{language === 'el' ? 'Τηλ. Οδικής Βοήθειας' : 'Assistance Phone'}</span>
                                    <span className="font-medium text-slate-800">{vehicle.assistance_phone || '-'}</span>
                                </div>
                            </div>

                            {/* Leasing Reminders */}
                            {vehicle.leasing_reminders && vehicle.leasing_reminders.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-slate-200">
                                    <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                                        <Bell className="w-4 h-4 text-purple-500" />
                                        {language === 'el' ? 'Υπενθυμίσεις Leasing' : 'Leasing Reminders'}
                                    </h4>
                                    <div className="space-y-2">
                                        {vehicle.leasing_reminders.map((reminder, idx) => (
                                            <div key={idx} className="text-sm p-2 bg-purple-50 rounded-lg">
                                                <div className="font-medium text-purple-700">{reminder.description}</div>
                                                <div className="text-xs text-purple-500">{formatDate(reminder.date)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* Photos Tab */}
                <TabsContent value="photos" className="mt-4">
                    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                        {allPhotos.length > 0 ? (
                            <div className="space-y-4">
                                {/* Filter Buttons */}
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                    <div className="flex items-center gap-2">
                                        <Filter className="w-4 h-4 text-slate-400" />
                                        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                                            <button
                                                onClick={() => { setPhotoFilter('all'); setSelectedPhotoIndex(0); }}
                                                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                                    photoFilter === 'all' 
                                                        ? 'bg-[#1B7EB5] text-white' 
                                                        : 'bg-white text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                {language === 'el' ? 'Όλες' : 'All'} ({allPhotos.length})
                                            </button>
                                            <button
                                                onClick={() => { setPhotoFilter('checkin'); setSelectedPhotoIndex(0); }}
                                                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                                                    photoFilter === 'checkin' 
                                                        ? 'bg-green-500 text-white' 
                                                        : 'bg-white text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                Check-In ({allPhotos.filter(p => p.type !== 'checkout').length})
                                            </button>
                                            <button
                                                onClick={() => { setPhotoFilter('checkout'); setSelectedPhotoIndex(0); }}
                                                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                                                    photoFilter === 'checkout' 
                                                        ? 'bg-orange-500 text-white' 
                                                        : 'bg-white text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                Check-Out ({allPhotos.filter(p => p.type === 'checkout').length})
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openZoomModal(selectedPhotoIndex)}
                                            className="flex items-center gap-1.5"
                                        >
                                            <ZoomIn className="w-4 h-4" />
                                            {language === 'el' ? 'Μεγέθυνση' : 'Zoom'}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => downloadPhoto(selectedPhotoIndex)}
                                            className="flex items-center gap-1.5"
                                        >
                                            <Download className="w-4 h-4" />
                                            {language === 'el' ? 'Λήψη' : 'Download'}
                                        </Button>
                                    </div>
                                </div>

                                {/* Main Photo Viewer */}
                                {filteredPhotos.length > 0 ? (
                                    <>
                                        <div className="relative bg-slate-100 rounded-xl overflow-hidden">
                                            <div className="aspect-video relative cursor-pointer" onClick={() => openZoomModal(selectedPhotoIndex)}>
                                                {getFilteredBlobUrl(selectedPhotoIndex) ? (
                                                    <img
                                                        src={getFilteredBlobUrl(selectedPhotoIndex)}
                                                        alt={`Vehicle photo ${selectedPhotoIndex + 1}`}
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23f1f5f9" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="16">No Image</text></svg>';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                                                    </div>
                                                )}
                                                <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-medium ${
                                                    filteredPhotos[selectedPhotoIndex]?.type === 'checkout' 
                                                        ? 'bg-orange-500 text-white' 
                                                        : 'bg-green-500 text-white'
                                                }`}>
                                                    {filteredPhotos[selectedPhotoIndex]?.type === 'checkout' ? 'Check-Out' : 'Check-In'}
                                                </div>
                                                <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 rounded-full text-sm text-white">
                                                    {filteredPhotos[selectedPhotoIndex]?.photo_type || filteredPhotos[selectedPhotoIndex]?.type}
                                                </div>
                                                <div className="absolute bottom-3 left-3 px-3 py-1 bg-black/60 rounded-full text-xs text-white">
                                                    {formatDateTime(filteredPhotos[selectedPhotoIndex]?.date)}
                                                </div>
                                                {/* Zoom hint */}
                                                <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/60 rounded-full text-xs text-white flex items-center gap-1">
                                                    <ZoomIn className="w-3 h-3" />
                                                    {language === 'el' ? 'Κλικ για μεγέθυνση' : 'Click to zoom'}
                                                </div>
                                            </div>
                                            {filteredPhotos.length > 1 && (
                                                <>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setSelectedPhotoIndex(prev => prev > 0 ? prev - 1 : filteredPhotos.length - 1); }}
                                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                                                    >
                                                        <ChevronLeft className="w-6 h-6" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setSelectedPhotoIndex(prev => prev < filteredPhotos.length - 1 ? prev + 1 : 0); }}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                                                    >
                                                        <ChevronRight className="w-6 h-6" />
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* Thumbnails */}
                                        <div className="flex gap-2 overflow-x-auto pb-2">
                                            {filteredPhotos.map((photo, idx) => {
                                                const originalIndex = allPhotos.findIndex(p => p === photo);
                                                return (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setSelectedPhotoIndex(idx)}
                                                        className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                                                            idx === selectedPhotoIndex ? 'border-[#1B7EB5] ring-2 ring-[#1B7EB5]/30' : 'border-transparent hover:border-slate-300'
                                                        }`}
                                                    >
                                                        {photoBlobUrls[originalIndex] ? (
                                                            <img
                                                                src={photoBlobUrls[originalIndex]}
                                                                alt={`Thumbnail ${idx + 1}`}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="%23f1f5f9" width="80" height="80"/></svg>';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                                                                <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                                                            </div>
                                                        )}
                                                        <div className={`absolute bottom-0 left-0 right-0 h-1 ${
                                                            photo.type === 'checkout' ? 'bg-orange-500' : 'bg-green-500'
                                                        }`} />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* Photo counter */}
                                        <div className="text-center text-sm text-slate-500">
                                            {selectedPhotoIndex + 1} / {filteredPhotos.length}
                                            {photoFilter !== 'all' && (
                                                <span className="ml-2 text-slate-400">
                                                    ({language === 'el' ? 'φιλτραρισμένες' : 'filtered'})
                                                </span>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-slate-400">
                                        <Camera className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                        <p>{language === 'el' ? 'Δεν υπάρχουν φωτογραφίες για αυτό το φίλτρο' : 'No photos for this filter'}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-slate-400">
                                <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>{language === 'el' ? 'Δεν υπάρχουν φωτογραφίες' : 'No photos available'}</p>
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Zoom Modal */}
                {zoomModalOpen && (
                    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" onClick={() => setZoomModalOpen(false)}>
                        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                            <button
                                onClick={(e) => { e.stopPropagation(); downloadPhoto(zoomPhotoIndex); }}
                                className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                                title={language === 'el' ? 'Λήψη' : 'Download'}
                            >
                                <Download className="w-6 h-6" />
                            </button>
                            <button
                                onClick={() => setZoomModalOpen(false)}
                                className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        {/* Photo info */}
                        <div className="absolute top-4 left-4 text-white z-10">
                            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${
                                filteredPhotos[zoomPhotoIndex]?.type === 'checkout' 
                                    ? 'bg-orange-500' 
                                    : 'bg-green-500'
                            }`}>
                                {filteredPhotos[zoomPhotoIndex]?.type === 'checkout' ? 'Check-Out' : 'Check-In'}
                            </div>
                            <div className="text-sm text-white/80">
                                {filteredPhotos[zoomPhotoIndex]?.photo_type} • {formatDateTime(filteredPhotos[zoomPhotoIndex]?.date)}
                            </div>
                        </div>

                        {/* Main image */}
                        <div className="relative w-full h-full flex items-center justify-center p-16" onClick={(e) => e.stopPropagation()}>
                            {getFilteredBlobUrl(zoomPhotoIndex) ? (
                                <img
                                    src={getFilteredBlobUrl(zoomPhotoIndex)}
                                    alt="Zoomed photo"
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <Loader2 className="w-12 h-12 text-white animate-spin" />
                            )}
                        </div>

                        {/* Navigation */}
                        {filteredPhotos.length > 1 && (
                            <>
                                <button
                                    onClick={(e) => { e.stopPropagation(); navigateZoom(-1); }}
                                    disabled={zoomPhotoIndex === 0}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition-colors"
                                >
                                    <ChevronLeft className="w-8 h-8" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); navigateZoom(1); }}
                                    disabled={zoomPhotoIndex === filteredPhotos.length - 1}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition-colors"
                                >
                                    <ChevronRight className="w-8 h-8" />
                                </button>
                            </>
                        )}

                        {/* Counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
                            {zoomPhotoIndex + 1} / {filteredPhotos.length}
                        </div>
                    </div>
                )}

                {/* Maintenance Tab */}
                <TabsContent value="maintenance" className="mt-4">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        {maintenanceRecords.length > 0 ? (
                            <div className="divide-y divide-slate-100">
                                {maintenanceRecords.map((record) => (
                                    <div key={record.id} className="p-4 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                    record.status === 'completed' ? 'bg-green-100' :
                                                    record.status === 'overdue' ? 'bg-red-100' : 'bg-yellow-100'
                                                }`}>
                                                    <Wrench className={`w-5 h-5 ${
                                                        record.status === 'completed' ? 'text-green-600' :
                                                        record.status === 'overdue' ? 'text-red-600' : 'text-yellow-600'
                                                    }`} />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-slate-800">{record.maintenance_type}</div>
                                                    <div className="text-sm text-slate-500">{record.description || '-'}</div>
                                                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                                                        <span>{language === 'el' ? 'Προγρ.' : 'Scheduled'}: {formatDate(record.scheduled_date)}</span>
                                                        {record.completed_date && (
                                                            <span>{language === 'el' ? 'Ολοκλ.' : 'Completed'}: {formatDate(record.completed_date)}</span>
                                                        )}
                                                        {record.cost && <span>€{record.cost}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                record.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                record.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                                record.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {record.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-slate-400">
                                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>{language === 'el' ? 'Δεν υπάρχουν εγγραφές συντήρησης' : 'No maintenance records'}</p>
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Damages Tab */}
                <TabsContent value="damages" className="mt-4">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        {damageAlerts.length > 0 ? (
                            <div className="divide-y divide-slate-100">
                                {damageAlerts.map((damage) => (
                                    <div key={damage.id} className={`p-4 ${
                                        damage.severity === 'high' ? 'bg-red-50' :
                                        damage.severity === 'medium' ? 'bg-orange-50' : 'bg-yellow-50'
                                    }`}>
                                        <div className="flex items-start gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                damage.severity === 'high' ? 'bg-red-200' :
                                                damage.severity === 'medium' ? 'bg-orange-200' : 'bg-yellow-200'
                                            }`}>
                                                <AlertTriangle className={`w-5 h-5 ${
                                                    damage.severity === 'high' ? 'text-red-600' :
                                                    damage.severity === 'medium' ? 'text-orange-600' : 'text-yellow-600'
                                                }`} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                        damage.severity === 'high' ? 'bg-red-200 text-red-800' :
                                                        damage.severity === 'medium' ? 'bg-orange-200 text-orange-800' : 'bg-yellow-200 text-yellow-800'
                                                    }`}>
                                                        {damage.severity === 'high' ? '🔴' : damage.severity === 'medium' ? '🟠' : '🟡'} {damage.severity.toUpperCase()}
                                                    </span>
                                                    <span className="text-xs text-slate-500">{damage.photo_type}</span>
                                                </div>
                                                <p className="text-slate-700">
                                                    {language === 'el' ? damage.damage_description_el : damage.damage_description_en}
                                                </p>
                                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                                    <span><User className="w-3 h-3 inline mr-1" />{damage.driver_name}</span>
                                                    <span><Calendar className="w-3 h-3 inline mr-1" />{formatDateTime(damage.created_at)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-slate-400">
                                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                                <p>{language === 'el' ? 'Δεν υπάρχουν αναφορές ζημιών' : 'No damage reports'}</p>
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Inspections History Tab */}
                <TabsContent value="inspections" className="mt-4">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        {inspections.length > 0 ? (
                            <div className="divide-y divide-slate-100">
                                {inspections.map((inspection) => (
                                    <div key={inspection.id} className="p-4 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                    inspection.checkout_completed ? 'bg-green-100' : 'bg-blue-100'
                                                }`}>
                                                    <Camera className={`w-5 h-5 ${
                                                        inspection.checkout_completed ? 'text-green-600' : 'text-blue-600'
                                                    }`} />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-slate-800">
                                                        {formatDate(inspection.created_at)}
                                                    </div>
                                                    <div className="text-sm text-slate-500">
                                                        {inspection.photos?.length || 0} {language === 'el' ? 'φωτογραφίες check-in' : 'check-in photos'}
                                                        {inspection.checkout_photos?.length > 0 && (
                                                            <> • {inspection.checkout_photos.length} {language === 'el' ? 'φωτογραφίες check-out' : 'check-out photos'}</>
                                                        )}
                                                    </div>
                                                    {inspection.fuel_level && (
                                                        <div className="text-xs text-slate-400 mt-1">
                                                            <Fuel className="w-3 h-3 inline mr-1" />
                                                            {fuelLevelConfig[inspection.fuel_level]?.label}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    inspection.checkout_completed ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {inspection.checkout_completed 
                                                        ? (language === 'el' ? 'Ολοκληρωμένο' : 'Completed')
                                                        : (language === 'el' ? 'Σε εξέλιξη' : 'In Progress')
                                                    }
                                                </span>
                                                {inspection.checkout_date && (
                                                    <div className="text-xs text-slate-400 mt-1">
                                                        Check-out: {formatDate(inspection.checkout_date)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-slate-400">
                                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>{language === 'el' ? 'Δεν υπάρχει ιστορικό ελέγχων' : 'No inspection history'}</p>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
